import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import type { Invoice, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { useProjectOptions } from '../hooks/useEntityOptions'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({ project_slug: '', due_date: '', tax_rate: 0 })
  const [saving, setSaving] = useState(false)
  const { options: projectOptions } = useProjectOptions()
  const navigate = useNavigate()

  const loadInvoices = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (status) params.set('status', status)
      const result = await api.get<PaginatedResult<Invoice>>(`/invoices?${params}`)
      setInvoices(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, status])

  useEffect(() => { loadInvoices() }, [loadInvoices])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const created = await api.post<{ slug?: string }>('/invoices', { ...formData, due_date: formData.due_date || undefined })
      setDrawerOpen(false)
      setFormData({ project_slug: '', due_date: '', tax_rate: 0 })
      if (created?.slug) navigate(`/invoices/${created.slug}`)
      else loadInvoices()
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Invoices</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Invoice</Button>
      </div>

      <div className="mb-4">
        <div className="v3-select-wrap max-w-[160px]">
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }} className="v3-select">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
          </select>
          <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
        </div>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : invoices.length === 0 ? (
        <EmptyState
          title="No invoices yet — create one when you're ready"
          description="Create your first invoice."
          action={<Button onClick={() => setDrawerOpen(true)}>New Invoice</Button>}
        />
      ) : (
        <>
          <GlowCard className="hidden sm:block" style={{ padding: 0, overflow: 'hidden' }} delay={100}>
            <table className="v3-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-inset)', borderBottom: '1px solid var(--border-light)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Invoice #</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Balance</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Due</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoices/${inv.slug}`)}
                    style={{ borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500, color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>{inv.invoice_number ?? inv.slug}</td>
                    <td style={{ padding: '10px 12px' }}><StatusBadge status={inv.status} /></td>
                    <td style={{ padding: '10px 12px', fontSize: 14, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{inv.invoice_type}</td>
                    <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>${inv.total.toLocaleString()}</td>
                    <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>${inv.balance_due.toLocaleString()}</td>
                    <td className="mono" style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlowCard>

          <div className="sm:hidden space-y-3">
            {invoices.map((inv, i) => (
              <GlowCard
                key={inv.id}
                onClick={() => navigate(`/invoices/${inv.slug}`)}
                style={{ padding: 12, cursor: 'pointer' }}
                delay={100 + i * 80}
              >
                <div className="flex justify-between items-start">
                  <p style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', color: 'var(--primary)' }}>{inv.invoice_number ?? inv.slug}</p>
                  <StatusBadge status={inv.status} />
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'capitalize' }}>{inv.invoice_type}</span>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>${inv.total.toLocaleString()}</span>
                </div>
              </GlowCard>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Invoice">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Project *</label>
            <div className="v3-select-wrap">
              <select value={formData.project_slug} onChange={(e) => setFormData({ ...formData, project_slug: e.target.value })} className="v3-select" required>
                <option value="">Select project...</option>
                {projectOptions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Due Date</label>
            <input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="v3-input" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Tax Rate (%)</label>
            <input type="number" step="0.01" value={formData.tax_rate} onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })} className="v3-input" />
          </div>
          <Button type="submit" disabled={saving} loading={saving} style={{ width: '100%' }}>
            Create Invoice
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
